import React, { useState } from 'react';
import {
    Box, Button, FormControl, InputLabel, MenuItem, Popover, Select, Typography,
} from '@mui/material';
import { withLanguage } from '../common/Texts';
import { resultsTokens as t } from './tokens';

function MultiSelect({ label, value, onChange, options }) {
    return (
        <FormControl size="small" fullWidth>
            <InputLabel>{label}</InputLabel>
            <Select
                multiple
                label={label}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                renderValue={(selected) => selected.map((id) => options.find((o) => o.id === id)?.name).filter(Boolean).join(', ')}
            >
                {options.map((o) => (
                    <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

export default function FiltersPopover({
    anchorPosition, onClose, language,
    businessUnits, positions, departments,
    selected, onChange, onClear,
}) {
    const tr = withLanguage(language);
    const open = Boolean(anchorPosition);
    const [draft, setDraft] = useState(selected);
    // Re-sync the draft from `selected` only when the popover transitions to
    // open. Adjusting state during render (React docs: "Storing information
    // from previous renders") avoids the cascading-render lint from useEffect.
    const [wasOpen, setWasOpen] = useState(open);
    if (wasOpen !== open) {
        setWasOpen(open);
        if (open) setDraft(selected);
    }

    const handleApply = () => {
        onChange(draft);
        onClose();
    };

    const handleClear = () => {
        setDraft({ bu: [], dept: [], pos: [] });
        onClear();
    };

    return (
        <Popover
            open={open}
            anchorReference="anchorPosition"
            anchorPosition={anchorPosition || { top: 0, left: 0 }}
            onClose={onClose}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{
                paper: {
                    sx: {
                        width: 360,
                        p: 2,
                        borderRadius: `${t.radius.lg}px`,
                        border: `1px solid ${t.color.border}`,
                        boxShadow: t.shadow.lg,
                    },
                },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: t.color.fg }}>
                    {tr('filters')}
                </Typography>
                <Button size="small" onClick={handleClear} sx={{ minWidth: 0, color: t.color.fgMuted }}>
                    {tr('clear_all')}
                </Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <MultiSelect
                    label={tr('business_unit')}
                    value={draft.bu}
                    onChange={(v) => setDraft({ ...draft, bu: v })}
                    options={businessUnits}
                />
                <MultiSelect
                    label={tr('area')}
                    value={draft.dept}
                    onChange={(v) => setDraft({ ...draft, dept: v })}
                    options={departments}
                />
                <MultiSelect
                    label={tr('position')}
                    value={draft.pos}
                    onChange={(v) => setDraft({ ...draft, pos: v })}
                    options={positions}
                />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                    variant="contained"
                    size="small"
                    onClick={handleApply}
                    sx={{
                        bgcolor: t.color.primary,
                        color: '#fff',
                        borderRadius: `${t.radius.md}px`,
                        px: 2,
                        py: 0.75,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: 'none',
                        '&:hover': { bgcolor: t.color.primary, boxShadow: 'none', filter: 'brightness(0.95)' },
                    }}
                >
                    {tr('apply')}
                </Button>
            </Box>
        </Popover>
    );
}
